import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useRAGStore } from '../store/ragStore';

interface TabNavigationContextType {
  selectedTabIndex: number;
  setSelectedTabIndex: (index: number) => void;
  navigateToTab: (tabName: string) => void;
}

const TabNavigationContext = createContext<TabNavigationContextType | undefined>(undefined);

export const useTabNavigation = () => {
  const context = useContext(TabNavigationContext);
  if (context === undefined) {
    throw new Error('useTabNavigation must be used within a TabNavigationProvider');
  }
  return context;
};

interface TabNavigationProviderProps {
  children: ReactNode;
}

export const TabNavigationProvider: React.FC<TabNavigationProviderProps> = ({ children }) => {
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const { currentContext } = useRAGStore();

  const navigateToTab = (tabName: string) => {
    const tabIndexMap: Record<string, number> = {
      'Context Management': 0,
      'Protected Keywords': 1,
      'Keyword Mappings': 2,
      'File Upload': 3,
      'File List': 4,
      'Custom RAG': 5,
      'Query': 6,
      'Ask AI': 7,
    };
    
    const targetIndex = tabIndexMap[tabName];
    if (targetIndex !== undefined) {
      // Only allow navigation to other tabs if context is selected
      if (targetIndex === 0 || currentContext) {
        setSelectedTabIndex(targetIndex);
      } else {
      }
    }
  };

  // Reset to Context Management tab if no context is selected
  useEffect(() => {
    if (!currentContext && selectedTabIndex !== 0) {
      setSelectedTabIndex(0);
    }
  }, [currentContext, selectedTabIndex]);

  return (
    <TabNavigationContext.Provider value={{
      selectedTabIndex,
      setSelectedTabIndex,
      navigateToTab,
    }}>
      {children}
    </TabNavigationContext.Provider>
  );
};
